import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, useReducedMotion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  Play, ArrowRight, School, Users, Rocket, 
  CheckCircle, Shield, Zap, TrendingUp, Award,
  ChevronRight, Star, Phone, Mail,
  MapPin, Cloud, Cpu, Lock, Headphones, Globe, Database,
  MonitorSmartphone, GraduationCap, Building2, BookOpen,
  Check, ChevronDown, Linkedin, Twitter, Facebook
} from 'lucide-react';
import { cn } from '@/lib/utils';
import NetworkMesh from '@/components/ui/NetworkMesh';
import { TypewriterText } from '@/components/ui/AnimatedText';
import { 
  HeroText,
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  InteractiveCard,
  MagneticButton,
  FloatingElement,
  GSAPCounter,
  NeonGlowLine,
  CurvedDivider,
  ParallaxBG,
  TextScramble,
  OptimizedMarquee,
} from '@/components/ui/OptimizedAnimations';
import { 
  CharacterStagger,
  TextGenerateEffect,
  LayeredParallax,
  WaveDivider,
  HalftonePattern,
  AppleCarousel,
  AppleCard,
  InfiniteMovingCards,
  SparklesCore,
  Spotlight,
} from '@/components/ui/AceternityAnimations';

gsap.registerPlugin(ScrollTrigger);

// =========================================
// DATA CONSTANTS
// =========================================

const trustedLogos = [
  { name: 'St. Charles Schools', color: 'teal' },
  { name: 'Timpany Group', color: 'orange' },
  { name: 'ACTS Institutions', color: 'teal' },
  { name: 'Vincent Pallotti', color: 'orange' },
  { name: 'St. Aloysius', color: 'teal' },
  { name: 'Little Flower Convent', color: 'orange' },
  { name: 'Bethany Schools', color: 'teal' },
  { name: 'ICHE Network', color: 'orange' },
];

const products = [
  {
    id: 'school-erp',
    name: 'Unified Global Platform',
    tagline: 'Complete K-12 Management',
    description: 'Seamlessly managed schools across US, Australia, & UAE with multi-curriculum support, global parent access, and unified dashboards.',
    icon: School,
    color: 'teal',
    features: ['Multi-Curriculum Support', 'Global Parent Access', 'Smart Attendance', 'Unified Dashboard'],
    image: '/images/products/global-features.png',
    objectPosition: 'left center',
    modules: ['US State Standards', 'Australian National Curriculum', 'UAE MOE Standards', 'Transport'],
    route: '/products/school-erp',
  },
  {
    id: 'college-erp',
    name: 'Precision Education',
    tagline: 'Higher Education Suite',
    description: 'OBE-aligned curriculum, outcomes, & achievement tracking with assessment alignment matrix, skill progress heatmap, and real-time analytics.',
    icon: GraduationCap,
    color: 'orange',
    features: ['Curriculum Mapping', 'Learning Outcomes', 'Assessment Alignment', 'Skill Progress Heatmap'],
    image: '/images/products/global-features.png',
    objectPosition: 'center center',
    modules: ['OBE Framework', 'Faculty Portal', 'Library System', 'Research Portal'],
    route: '/products/college-erp',
  },
  {
    id: 'lynkt',
    name: 'Global Mobility Hub',
    tagline: 'International Student Management',
    description: 'Effortless international student management & multi-currency tuition with visa support, cultural integration programs, and school ledger.',
    icon: Users,
    color: 'teal',
    features: ['Visa & Immigration', 'Multi-Currency Tuition', 'Cultural Integration', 'International Advisor'],
    image: '/images/products/global-features.png',
    objectPosition: 'right center',
    route: '/products/lynkt',
  },
  {
    id: 'lms',
    name: 'LMS',
    tagline: 'Learning Management',
    description: 'AI-powered learning platform with personalized learning paths, assessments, and real-time progress tracking.',
    icon: BookOpen,
    color: 'orange',
    features: ['AI Tutoring', 'Video Library', 'Assessments', 'Progress Tracking'],
    image: '/images/products/lms.png',
    route: '/products/lms',
  },
];

const stats = [
  { value: '500', suffix: '+', label: 'Institutions' },
  { value: '10', suffix: 'L+', label: 'Students Impacted' },
  { value: '15000', suffix: '+', label: 'Teachers Empowered' },
  { value: '99.9', suffix: '%', label: 'Uptime' },
];

const tattvaData = [
  { type: 'jiva', title: 'Jiva', subtitle: 'Innovation & Creativity', description: 'We breathe life into static code through continuous innovation.', color: '#FF7043', index: 0 },
  { type: 'ajiva', title: 'Ajiva', subtitle: 'Technical Excellence', description: '99.9% Uptime. Zero Compromise on quality.', color: '#808C9B', index: 1 },
  { type: 'ashrava', title: 'Ashrava', subtitle: 'Continuous Learning', description: 'Data that flows into actionable wisdom.', color: '#3B82F6', index: 2 },
  { type: 'bandha', title: 'Bandha', subtitle: 'Strong Partnerships', description: 'Bonds that empower institutional growth.', color: '#A855F7', index: 3 },
  { type: 'samvara', title: 'Samvara', subtitle: 'Data Security', description: 'Protected pathways to digital success.', color: '#22C55E', index: 4 },
  { type: 'nirjara', title: 'Nirjara', subtitle: 'User Experience', description: 'Shedding complexity for ultimate clarity.', color: '#EAB308', index: 5 },
  { type: 'moksha', title: 'Moksha', subtitle: 'Ultimate Liberation', description: 'Transforming potential into achievement.', color: '#00897B', index: 6 },
];

const nepModules = [
  { id: 'placement', name: 'Placement Management', icon: Rocket },
  { id: 'outcome', name: 'Outcome Based Education', icon: TrendingUp },
  { id: 'credit', name: 'Credit Transfer System', icon: Database },
  { id: 'naac', name: 'NAAC & NBA Compliance', icon: Award },
  { id: 'research', name: 'Research Management', icon: BookOpen },
];

const whyChooseUs = [
  { icon: Cloud, title: 'Cloud-Native', desc: 'Scalable infrastructure that grows with you', color: 'teal' },
  { icon: Cpu, title: 'AI-First', desc: 'Intelligent automation at every step', color: 'orange' },
  { icon: Lock, title: 'ISO Security', desc: 'Enterprise-grade data protection', color: 'teal' },
  { icon: Headphones, title: '24/7 Support', desc: 'Dedicated success managers', color: 'orange' },
];

const featureTags = ['Fast Deployment', 'Multi-Language', 'Data Sovereignty', 'API-First Design'];

const beforeAfterData = {
  before: [
    'Scattered data across spreadsheets',
    'Manual attendance tracking',
    'Paper-based fee collection',
    'No real-time insights',
    'Communication gaps with parents',
  ],
  after: [
    'Unified data dashboard',
    'Biometric & AI attendance',
    'Digital payment gateway',
    'Predictive analytics',
    'Instant parent notifications',
  ],
};

const awards = [
  { name: 'Ashoka Awards', year: '2024' },
  { name: 'DIDAC India', year: '2025' },
  { name: 'Top 10 EdTech', year: '2024' },
  { name: 'Iconic Gold Award', year: '2025' },
];

const testimonials = [
  // Featured Client Schools
  {
    quote: "Tattva Techworkz has revolutionized our school administration. The seamless integration of fee management, attendance, and parent communication has reduced our administrative burden by 60%. Their support team is exceptional.",
    author: "Ms. Deepali Diaz",
    role: "Principal, ACTS Secondary School, Hosur Road",
    rating: 5,
    image: null,
  },
  {
    quote: "As a Director overseeing multiple institutions, I needed a unified platform that could scale. Tattva's ERP delivered exactly that — real-time insights across all our campuses with enterprise-grade security.",
    author: "Mr. Selvaraj",
    role: "Director, ACTS Senior Secondary School",
    rating: 5,
    image: null,
  },
  {
    quote: "The system has transformed how we manage our girls' school. From admission to graduation tracking, every process is now streamlined. The parent portal has especially improved our home-school connection.",
    author: "Sister Cynthia",
    role: "Principal, Gulabi Girls High School",
    rating: 5,
    image: null,
  },
  {
    quote: "We've been using Tattva's School ERP for over a year now. The automation of daily tasks has freed our teachers to focus on what matters most — teaching. Highly recommended for convent schools.",
    author: "Sister Helena",
    role: "Principal, Mariam Nivas School",
    rating: 5,
    image: null,
  },
  {
    quote: "The fee management and transport modules have completely eliminated manual errors. Parents appreciate the transparency, and our collection efficiency has improved significantly.",
    author: "Mr. Salman",
    role: "Principal, Holy Saint Public School",
    rating: 5,
    image: null,
  },
  {
    quote: "Tattva's ERP has modernized our century-old institution without compromising our values. The team understood our unique needs and delivered a solution that respects tradition while embracing technology.",
    author: "Sr. Clita Lobo",
    role: "Principal, St. Charles High School, Kammanahalli",
    rating: 5,
    image: null,
  },
  // St. Charles Network
  {
    quote: "Managing multiple St. Charles institutions across Karnataka became effortless with Tattva's centralized dashboard. The province-wide reporting has improved our governance significantly.",
    author: "Sr. Superior General",
    role: "St. Charles Borromeo Western Province, Bengaluru",
    rating: 5,
    image: null,
  },
  {
    quote: "The ICSE board requirements are complex, but Tattva's system handles everything from CCE to board exam registration seamlessly. Our teachers love the gradebook module.",
    author: "Fr. Principal",
    role: "St. Charles ICSE School, Lingarajapuram",
    rating: 5,
    image: null,
  },
  // Timpany Schools
  {
    quote: "Timpany Schools across Vizag are now fully digitized. The transport fee collection module alone has recovered lakhs in pending dues. Tattva understands scale.",
    author: "School Administrator",
    role: "Timpany Senior Secondary School, Visakhapatnam",
    rating: 5,
    image: null,
  },
  {
    quote: "From pre-primary to senior secondary, we needed one system that grows with our students. Tattva delivered a unified solution that our entire Timpany network relies on daily.",
    author: "Academic Director",
    role: "Timpany Group of Schools",
    rating: 5,
    image: null,
  },
  // Vincent Pallotti
  {
    quote: "The integration between our school, PU college, and degree college is now seamless. Student data flows automatically as they progress through our institution. Truly unified.",
    author: "College Principal",
    role: "St. Vincent Pallotti School & College, Bangalore",
    rating: 5,
    image: null,
  },
  // St. Aloysius
  {
    quote: "Managing three levels of education — school, PU, and degree — in one platform seemed impossible until Tattva. The academic credit system and outcome-based education modules are game-changers.",
    author: "Director of Academics",
    role: "St. Aloysius Institutions, Bangalore",
    rating: 5,
    image: null,
  },
  // Little Flower Convent
  {
    quote: "Our convent schools in Ranipet and Ammoor now operate with complete transparency. The mobile app keeps our teaching sisters connected to every classroom activity.",
    author: "Mother Superior",
    role: "Little Flower Convent Schools, Tamil Nadu",
    rating: 5,
    image: null,
  },
  // St. Teresa's
  {
    quote: "St. Teresa's has served women's education for decades. Tattva's ERP has helped us bring that legacy into the digital age while maintaining the personal touch our students deserve.",
    author: "College Administrator",
    role: "St. Teresa's Composite PU & Degree College, Chamrajpet",
    rating: 5,
    image: null,
  },
  // Sophia School
  {
    quote: "The LMS integration has transformed our teaching methodology. Students access quality content anytime, and our teachers can track engagement in real-time. Excellent for blended learning.",
    author: "Academic Coordinator",
    role: "Sophia School, Bangalore",
    rating: 5,
    image: null,
  },
  // Mary Immaculate
  {
    quote: "In Shimoga, finding reliable ed-tech support was challenging. Tattva's 24/7 assistance and vernacular language support made the transition smooth for our entire staff.",
    author: "School Principal",
    role: "Mary Immaculate School, Shimoga",
    rating: 5,
    image: null,
  },
  // Bethany Congregation
  {
    quote: "The congregation management alongside school ERP is unique to our needs. Tattva customized the solution to handle both administrative and pastoral requirements beautifully.",
    author: "Provincial Superior",
    role: "Bethany Congregation Schools, Bangalore",
    rating: 5,
    image: null,
  },
  // Vidya Jyothi
  {
    quote: "Running a school in rural Bidar has its challenges. Tattva's offline-capable mobile app ensures our teachers can work even with intermittent connectivity. Thoughtful design.",
    author: "School Head",
    role: "Vidya Jyothi School, Hulsoor, Bidar",
    rating: 5,
    image: null,
  },
  // Jesuits Madurai
  {
    quote: "The Jesuit tradition of educational excellence found a worthy technology partner in Tattva. Their respect for our mission and values shows in every feature they built for us.",
    author: "Fr. Rector",
    role: "Jesuit Institutions, Madurai",
    rating: 5,
    image: null,
  },
  // Extramile & Twinkle
  {
    quote: "Pre-school management is unique — parent communication is everything. Tattva's photo sharing, daily reports, and milestone tracking keep our parents engaged and happy.",
    author: "Centre Director",
    role: "Extramile Edutainment & Twinkle Toddlers, Bangalore",
    rating: 5,
    image: null,
  },
  // ICHE Network & ETASI
  {
    quote: "As an education network serving Christian institutions across India, we needed a partner who could scale nationally. Tattva powers hundreds of our member schools reliably.",
    author: "Network Administrator",
    role: "ICHE Network & ETASI",
    rating: 5,
    image: null,
  },
  // From client testimonials
  {
    quote: "What once required multiple staff members can now be efficiently managed by a single staff. The School ERP has streamlined our processes and reduced manual effort through automation and real-time reporting.",
    author: "Rajashekhar Bingolli",
    role: "Principal, BIPS Guledgudda",
    rating: 5,
    image: null,
  },
  {
    quote: "We appreciate the support and partnership. Their contribution helped us create a high-quality publication for our students and staff, and we look forward to continuing our association in the future.",
    author: "Niraja Srinivasan",
    role: "Principal, Chinmaya Vidyalaya",
    rating: 5,
    image: null,
  },
  {
    quote: "We've been using the School ERP for over 10 years: a user-friendly, customizable platform streamlining our operations — inventory, front office, and data management. Their team offers excellent support.",
    author: "Anupama Sethi",
    role: "Principal, New Horizon Public School",
    rating: 5,
    image: null,
  },
  {
    quote: "The platform is loaded with features and functionalities that can truly ease the online management of schools! The best part is the excellent support they provide. Their team is highly professional, approachable, and quick.",
    author: "Anitha Ojha",
    role: "Admin, Tunbridge High School",
    rating: 5,
    image: null,
  },
  {
    quote: "Their customer service has been truly excellent and friendly. The support team has been available at all times, to us as well as to parents. With special mention of their responses being quick, appropriate, and timely.",
    author: "Dr. Jerry George Matthew",
    role: "Principal, Clarence High School",
    rating: 5,
    image: null,
  },
  {
    quote: "The platform has greatly enhanced our parent-school communication and parent engagement. Their security, privacy, and responsive support have been invaluable. We thank the team for their outstanding service and commitment.",
    author: "Thushara Nair",
    role: "Principal, New Horizon Vidya Mandir",
    rating: 5,
    image: null,
  },
];

const cycleTexts = [
  'Intelligent Campuses',
  'Empowered Teachers',
  'Job-Ready Students',
  'Future Leaders',
];

const digitalTransformationItems = [
  { id: 'k12', title: 'K-12', description: 'Complete school management', icon: School },
  { id: 'universities', title: 'Universities', description: 'Higher education excellence', icon: GraduationCap, featured: true },
  { id: 'corporate', title: 'Corporate', description: 'Training & development', icon: Building2 },
  { id: 'lms', title: 'LMS', description: 'Learning management', icon: BookOpen },
];

// =========================================
// FLOATING SHAPES COMPONENT
// =========================================
const FloatingShapes = () => {
  const prefersReducedMotion = useReducedMotion();
  
  if (prefersReducedMotion) return null;
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <FloatingElement duration={8} distance={20} delay={0} className="absolute top-[10%] left-[5%] opacity-10">
        <div className="w-64 h-64 border border-teal/30 rounded-full" />
      </FloatingElement>
      
      <FloatingElement duration={6} distance={15} delay={1} className="absolute top-[30%] right-[10%] opacity-10">
        <div className="w-32 h-32 border border-orange/30 rounded-full" />
      </FloatingElement>
      
      <FloatingElement duration={10} distance={10} delay={2} className="absolute bottom-[20%] left-[15%] opacity-5">
        <div className="w-48 h-48 grid grid-cols-4 gap-2">
          {Array.from({ length: 16 }).map((_, i) => (
            <div key={i} className="w-2 h-2 bg-teal rounded-full" />
          ))}
        </div>
      </FloatingElement>
      
      <FloatingElement duration={7} distance={12} delay={0.5} className="absolute top-[60%] right-[5%] opacity-10">
        <div className="w-40 h-40 relative">
          {Array.from({ length: 5 }).map((_, i) => (
            <div 
              key={i} 
              className="absolute w-full h-px bg-gradient-to-r from-transparent via-teal/50 to-transparent"
              style={{ top: `${i * 25}%`, transform: 'rotate(-45deg)' }}
            />
          ))}
        </div>
      </FloatingElement>
    </div>
  );
};

// =========================================
// TRUSTED BY LOGO COMPONENT
// =========================================
const TrustedLogo = ({ name, color }) => (
  <div className={cn(
    'flex items-center gap-2 px-6 py-3 rounded-full border transition-all duration-300',
    'hover:scale-105',
    color === 'teal' 
      ? 'border-teal/30 hover:border-teal/50 hover:bg-teal/5' 
      : 'border-orange/30 hover:border-orange/50 hover:bg-orange/5'
  )}>
    <div className={cn(
      'w-8 h-8 rounded-lg flex items-center justify-center',
      color === 'teal' ? 'bg-teal/20' : 'bg-orange/20'
    )}>
      <GraduationCap className={cn('w-4 h-4', color === 'teal' ? 'text-teal' : 'text-orange')} />
    </div>
    <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">{name}</span>
  </div>
);

// =========================================
// PRODUCT CARD COMPONENT
// =========================================
const ProductCard = ({ product, index }) => (
  <ScrollReveal delay={index * 0.1} distance={40}>
    <InteractiveCard
      hoverScale={1.02}
      hoverY={-6}
      glowColor={product.color === 'teal' ? 'rgba(0,137,123,0.3)' : 'rgba(255,112,67,0.3)'}
    >
      <div className={cn(
        'glass-card overflow-hidden h-full',
        product.color === 'teal' ? 'hover:border-teal/30' : 'hover:border-orange/30'
      )}>
        <div className="relative h-48 overflow-hidden">
          <div className={cn(
            'w-full h-full',
            product.color === 'teal'
              ? 'bg-gradient-to-br from-teal/10 via-void-light to-teal/5'
              : 'bg-gradient-to-br from-orange/10 via-void-light to-orange/5'
          )} />
          <div className="absolute inset-0 flex items-center justify-center">
            <product.icon className={cn('w-16 h-16 opacity-20', product.color === 'teal' ? 'text-teal' : 'text-orange')} />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-void via-void/50 to-transparent" />
          
          <div className={cn(
            'absolute top-4 right-4 px-3 py-1 rounded-full backdrop-blur-md border text-xs font-mono',
            product.color === 'teal' 
              ? 'bg-teal/20 border-teal/30 text-teal' 
              : 'bg-orange/20 border-orange/30 text-orange'
          )}>
            LIVE
          </div>

          <div className={cn(
            'absolute bottom-4 left-4 w-12 h-12 rounded-xl flex items-center justify-center',
            product.color === 'teal' ? 'bg-teal/20 text-teal' : 'bg-orange/20 text-orange'
          )}>
            <product.icon className="w-6 h-6" />
          </div>
        </div>

        <div className="p-6">
          <h3 className="text-display text-xl text-foreground mb-1">{product.name}</h3>
          <p className={cn(
            'text-sm font-medium mb-3',
            product.color === 'teal' ? 'text-teal' : 'text-orange'
          )}>
            {product.tagline}
          </p>
          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{product.description}</p>

          <div className="flex flex-wrap gap-2">
            {product.features.slice(0, 3).map((feature) => (
              <span 
                key={feature} 
                className="text-xs px-2 py-1 rounded-full bg-glass-surface border border-glass-border text-muted-foreground"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>
    </InteractiveCard>
  </ScrollReveal>
);

// =========================================
// TATTVA CARD COMPONENT (Optimized)
// =========================================
const TattvaCard = ({ tattva, index }) => (
  <InteractiveCard
    hoverScale={1.03}
    hoverY={-8}
    glowColor={`${tattva.color}40`}
    className="w-full"
  >
    <div
      className="glass-card h-[280px] p-6 relative overflow-hidden"
      style={{ borderColor: `${tattva.color}30` }}
    >
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `radial-gradient(circle at 50% 50%, ${tattva.color}20 0%, transparent 70%)` }}
      />

      <div className="absolute top-4 right-4">
        <TextScramble 
          text={`0${index + 1}`}
          className="text-4xl font-display font-bold text-foreground/10"
          trigger="scroll"
        />
      </div>

      <div className="relative z-10 h-full flex flex-col">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
          style={{ backgroundColor: `${tattva.color}20` }}
        >
          <span className="text-xl font-display font-bold" style={{ color: tattva.color }}>
            {tattva.title.charAt(0)}
          </span>
        </div>

        <h3 className="text-display text-xl text-foreground mb-1">{tattva.title}</h3>
        <p className="text-base font-medium mb-3" style={{ color: tattva.color }}>
          {tattva.subtitle}
        </p>
        <p className="text-muted-foreground text-sm leading-relaxed flex-1">
          {tattva.description}
        </p>

        <div 
          className="w-full h-1 rounded-full mt-4"
          style={{ backgroundColor: `${tattva.color}30` }}
        />
      </div>
    </div>
  </InteractiveCard>
);

// =========================================
// NEP MODULE SELECTOR COMPONENT
// =========================================
const NepModuleSelector = () => {
  const [activeModule, setActiveModule] = useState('placement');

  const moduleDetails = {
    placement: {
      title: 'Placement Module',
      description: 'End-to-end placement management with company portal, resume builder, and analytics dashboard. Track placement drives, schedule interviews, and generate comprehensive reports.',
      features: ['Company Portal', 'Resume Builder', 'Analytics Dashboard'],
    },
    outcome: {
      title: 'Outcome Based Education',
      description: 'Map course outcomes to program outcomes with automated attainment calculation. Generate OBE reports for accreditation compliance.',
      features: ['CO-PO Mapping', 'Attainment Calculator', 'Report Generator'],
    },
    credit: {
      title: 'Credit Transfer System',
      description: 'Seamless credit transfer between institutions as per NEP 2020 guidelines. Academic bank of credits integration ready.',
      features: ['ABC Integration', 'Credit Mapping', 'Transfer Tracking'],
    },
    naac: {
      title: 'NAAC & NBA Compliance',
      description: 'Automated data collection and report generation for NAAC and NBA accreditation. SSR and SAR preparation made simple.',
      features: ['SSR Generator', 'Data Analytics', 'Document Manager'],
    },
    research: {
      title: 'Research Management',
      description: 'Track research publications, patents, and projects. Manage research grants and generate impact reports.',
      features: ['Publication Tracker', 'Grant Management', 'Impact Analysis'],
    },
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      {/* Module List */}
      <div className="space-y-3">
        {nepModules.map((module) => (
          <motion.button
            key={module.id}
            onClick={() => setActiveModule(module.id)}
            className={cn(
              'w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-300 text-left',
              'border interactive-element',
              activeModule === module.id
                ? 'bg-teal/10 border-teal/30 text-teal'
                : 'bg-glass-surface border-glass-border text-muted-foreground hover:text-foreground hover:border-muted-foreground'
            )}
            whileHover={{ x: 5 }}
            transition={{ duration: 0.15 }}
          >
            <div className={cn(
              'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
              activeModule === module.id ? 'border-teal bg-teal' : 'border-muted-foreground'
            )}>
              {activeModule === module.id && <Check className="w-3 h-3 text-primary-foreground" />}
            </div>
            <module.icon className="w-5 h-5" />
            <span className="font-medium">{module.name}</span>
          </motion.button>
        ))}
      </div>

      {/* Module Details */}
      <motion.div
        key={activeModule}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="glass-card p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-teal/20 flex items-center justify-center">
            <Rocket className="w-5 h-5 text-teal" />
          </div>
          <h4 className="text-display text-xl text-foreground">
            {moduleDetails[activeModule].title}
          </h4>
        </div>
        <p className="text-muted-foreground mb-6">
          {moduleDetails[activeModule].description}
        </p>
        <div className="flex flex-wrap gap-2">
          {moduleDetails[activeModule].features.map((feature) => (
            <span 
              key={feature}
              className="px-4 py-2 rounded-full bg-teal/10 text-teal text-sm font-medium"
            >
              {feature}
            </span>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

// =========================================
// MAIN HOME PAGE COMPONENT
// =========================================
const HomePage = () => {
  const heroRef = useRef(null);
  const tattvaContainerRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  
  const { scrollYProgress: heroScrollProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const heroOpacity = useTransform(heroScrollProgress, [0, 0.5], [1, 0]);
  const heroY = useTransform(heroScrollProgress, [0, 0.5], [0, 50]);


  return (
    <div className="relative bg-void">
      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Spotlight Effect */}
        <Spotlight className="-top-40 left-0 md:left-60 md:-top-20" fill="hsl(174, 100%, 27%)" />
        
        {/* Background with Layered Parallax */}
        <LayeredParallax className="absolute inset-0">
          <div className="absolute inset-0">
            <NetworkMesh className="absolute inset-0" />
            <div className="absolute inset-0 bg-gradient-to-b from-void/30 via-void/60 to-void" />
          </div>
        </LayeredParallax>

        {/* Halftone Pattern Overlay */}
        <HalftonePattern className="z-[1]" animated={true} />

        {/* Sparkles */}
        <div className="absolute inset-0 z-[2]">
          <SparklesCore
            id="heroSparkles"
            background="transparent"
            minSize={0.4}
            maxSize={1}
            particleDensity={50}
            particleColor="#00897B"
          />
        </div>

        <FloatingShapes />

        {/* Contact Sidebar */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-4 z-20">
          {[
            { icon: Phone, label: 'Call' },
            { icon: Mail, label: 'Email' },
          ].map((item) => (
            <motion.button
              key={item.label}
              whileHover={{ scale: 1.1, x: -5 }}
              className="w-12 h-12 rounded-xl bg-glass-surface backdrop-blur-md border border-glass-border flex items-center justify-center text-muted-foreground hover:text-teal transition-colors"
            >
              <item.icon className="w-5 h-5" />
            </motion.button>
          ))}
        </div>

        {/* Stats Sidebar */}
        <div className="absolute right-4 bottom-32 hidden lg:flex flex-col gap-2 z-20 text-right">
          <div>
            <span className="text-2xl font-display font-bold text-teal">100k+</span>
            <p className="text-xs text-muted-foreground">Daily Users</p>
          </div>
          <div>
            <span className="text-2xl font-display font-bold text-orange">700k+</span>
            <p className="text-xs text-muted-foreground">Active Users</p>
          </div>
        </div>

        <motion.div 
          style={{ 
            opacity: prefersReducedMotion ? 1 : heroOpacity, 
            y: prefersReducedMotion ? 0 : heroY 
          }}
          className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20"
        >
          {/* Technical Badge */}
          <ScrollReveal delay={0.1} distance={20} className="flex justify-center mb-8">
            <span className="font-mono text-xs text-muted-foreground tracking-widest px-4 py-2 border border-glass-border rounded-full bg-glass-surface backdrop-blur-sm">
              TRANSFORMING EDUCATION TECHNOLOGY
            </span>
          </ScrollReveal>

          {/* Main Headline with Character Stagger */}
          <div className="text-center">
            <h1 className="text-[1.75rem] sm:text-5xl md:text-6xl lg:text-7xl font-display font-bold text-foreground mb-2">
              <CharacterStagger
                text="Intelligent Campus ERP &"
                delay={0.2}
                staggerDelay={0.025}
              />
            </h1>
            <h1 className="text-[1.75rem] sm:text-5xl md:text-6xl lg:text-7xl font-display font-bold text-gradient-teal mb-6">
              <CharacterStagger
                text="AI-Driven Career Ecosystem"
                delay={0.8}
                staggerDelay={0.025}
              />
            </h1>

            {/* Subheading */}
            <ScrollReveal delay={1.5} className="mb-10">
              <p className="text-muted-foreground text-lg sm:text-xl max-w-3xl mx-auto">
                Be future-ready with our next-gen technology. Join the revolution.
              </p>
            </ScrollReveal>

            {/* CTA Buttons */}
            <ScrollReveal delay={0.9}>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <MagneticButton strength={0.2}>
                  <Link to="/contact">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="btn-primary text-base px-8 py-4 shadow-teal-glow interactive-element"
                      data-testid="hero-cta-trial"
                    >
                      Start Free Trial
                      <ArrowRight className="w-5 h-5" />
                    </motion.button>
                  </Link>
                </MagneticButton>
                
                <MagneticButton strength={0.2}>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="btn-secondary text-base px-8 py-4 interactive-element"
                    data-testid="hero-cta-learn"
                  >
                    Learn more
                    <ChevronDown className="w-5 h-5" />
                  </motion.button>
                </MagneticButton>
              </div>
            </ScrollReveal>
          </div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              className="w-6 h-10 border-2 border-muted-foreground/30 rounded-full flex justify-center pt-2"
            >
              <motion.div className="w-1.5 h-1.5 bg-teal rounded-full" />
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* ============================================
          TRUSTED BY SECTION
          ============================================ */}
      <section className="py-12 bg-void border-b border-glass-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-8">
            <p className="text-sm text-muted-foreground">
              Trusted by <span className="text-teal font-semibold">500+</span> leading institutions worldwide
            </p>
          </ScrollReveal>
          
          <InfiniteMovingCards
            items={trustedLogos.map((logo) => (
              <TrustedLogo key={logo.name} name={logo.name} color={logo.color} />
            ))}
            direction="left"
            speed="slow"
          />
        </div>
      </section>

      {/* ============================================
          PRODUCT SUITE SECTION - Apple Cards Carousel
          ============================================ */}
      <section className="py-24 bg-void-light/30 relative">
        {/* Halftone Pattern */}
        <HalftonePattern color="hsl(17, 100%, 63%)" animated={true} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal className="text-center mb-6">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Our Products
            </span>
            <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
              Comprehensive Education
              <br />
              <span className="text-gradient-teal">Management Software Suite</span>
            </h2>
          </ScrollReveal>

          {/* Product Tags */}
          <ScrollReveal delay={0.2} className="flex flex-wrap justify-center gap-3 mb-8">
            {['End-to-end Solutions', 'Real-time Analytics', 'Cloud-native Infrastructure', 'Mobile-first Design'].map((tag) => (
              <span 
                key={tag}
                className="px-4 py-2 rounded-full border border-glass-border bg-glass-surface text-sm text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </ScrollReveal>

          <p className="text-center text-muted-foreground max-w-3xl mx-auto mb-8">
            From K-12 schools to universities, our integrated suite of products covers every aspect 
            of educational institution management with AI-powered automation and insights.
          </p>

          {/* Apple Cards Carousel */}
          <AppleCarousel
            items={products.map((product, index) => (
              <AppleCard
                key={product.id}
                index={index}
                card={{
                  src: product.image,
                  icon: product.icon,
                  color: product.color,
                  title: product.name,
                  category: product.tagline,
                  hideOverlayText: !!product.objectPosition,
                  objectPosition: product.objectPosition,
                  content: (
                    <div className="space-y-6">
                      <p className="text-muted-foreground text-lg">{product.description}</p>
                      <div>
                        <h4 className="text-foreground font-display font-semibold mb-3">Key Features</h4>
                        <div className="grid grid-cols-2 gap-3">
                          {product.features.map((feature) => (
                            <div key={feature} className="flex items-center gap-2">
                              <CheckCircle className={cn('w-4 h-4', product.color === 'teal' ? 'text-teal' : 'text-orange')} />
                              <span className="text-sm text-muted-foreground">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      {product.modules && (
                        <div>
                          <h4 className="text-foreground font-display font-semibold mb-3">Modules</h4>
                          <div className="flex flex-wrap gap-2">
                            {product.modules.map((module) => (
                              <span 
                                key={module}
                                className={cn(
                                  'px-3 py-1 rounded-full text-sm',
                                  product.color === 'teal' ? 'bg-teal/20 text-teal' : 'bg-orange/20 text-orange'
                                )}
                              >
                                {module}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <Link 
                        to={`/products/${product.id}`}
                        className={cn(
                          'inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all',
                          product.color === 'teal' 
                            ? 'bg-teal text-primary-foreground hover:bg-teal-light' 
                            : 'bg-orange text-primary-foreground hover:bg-orange-light'
                        )}
                      >
                        Learn More <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  ),
                }}
              />
            ))}
          />
        </div>
      </section>

      {/* Wave Divider */}
      <WaveDivider fillColor="hsl(215, 50%, 6%)" waveColor="hsl(174, 100%, 27%)" />

      {/* ============================================
          7 TATTVA FRAMEWORK - Horizontal Scroll
          ============================================ */}
      <section ref={tattvaContainerRef} className="relative py-24 bg-void">
        {/* Halftone overlay */}
        <HalftonePattern color="hsl(174, 100%, 27%)" animated={true} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal className="text-center mb-12">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Our Philosophy
            </span>
            <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
              The 7 <span className="text-gradient-teal">Tattva</span> Framework
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Ancient wisdom meets modern technology. Each Tattva represents a core principle
              that guides our platform development.
            </p>
          </ScrollReveal>

          {/* Tattva Cards Grid */}
          <StaggerContainer staggerDelay={0.08} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {tattvaData.slice(0, 4).map((tattva, index) => (
              <StaggerItem key={tattva.type}>
                <TattvaCard tattva={tattva} index={index} />
              </StaggerItem>
            ))}
          </StaggerContainer>
          <StaggerContainer staggerDelay={0.08} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 max-w-4xl mx-auto">
            {tattvaData.slice(4).map((tattva, index) => (
              <StaggerItem key={tattva.type}>
                <TattvaCard tattva={tattva} index={index + 4} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ============================================
          NEP 2020 MODULES SECTION
          ============================================ */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
              NEP 2020 Ready
            </span>
            <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
              Advanced Modules for
              <br />
              <span className="text-gradient-orange">NEP 2020 &amp; Accreditation</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Stay compliant with the latest education policies while streamlining accreditation processes.
            </p>
          </ScrollReveal>

          <NepModuleSelector />
        </div>
      </section>

      {/* Wave Divider */}
      <WaveDivider fillColor="hsl(215, 50%, 6%)" waveColor="hsl(17, 100%, 63%)" flip={true} />

      {/* ============================================
          WHY CHOOSE US SECTION
          ============================================ */}
      <section className="py-24 bg-void relative">
        <HalftonePattern color="hsl(174, 100%, 27%)" animated={true} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
              Why Institutions Switch to
              <br />
              <span className="text-gradient-teal">Tattva Techworkz</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Join 500+ institutions that have transformed their operations with our platform.
            </p>
          </ScrollReveal>

          {/* Feature Cards */}
          <StaggerContainer staggerDelay={0.1} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {whyChooseUs.map((item) => (
              <StaggerItem key={item.title}>
                <InteractiveCard hoverScale={1.03} hoverY={-6} glowColor={item.color === 'teal' ? 'rgba(0,137,123,0.2)' : 'rgba(255,112,67,0.2)'}>
                  <div className="glass-card p-6 h-full text-center">
                    <div className={cn(
                      'w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4',
                      item.color === 'teal' ? 'bg-teal/20' : 'bg-orange/20'
                    )}>
                      <item.icon className={cn('w-7 h-7', item.color === 'teal' ? 'text-teal' : 'text-orange')} />
                    </div>
                    <h4 className="text-display text-lg text-foreground mb-2">{item.title}</h4>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </InteractiveCard>
              </StaggerItem>
            ))}
          </StaggerContainer>

          {/* Feature Tags */}
          <ScrollReveal className="flex flex-wrap justify-center gap-3">
            {featureTags.map((tag) => (
              <span 
                key={tag}
                className="px-6 py-3 rounded-full border border-teal/30 bg-teal/5 text-teal text-sm font-medium"
              >
                {tag}
              </span>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Wave Divider */}
      <WaveDivider fillColor="hsl(215, 50%, 8%)" waveColor="hsl(174, 100%, 27%)" />

      {/* ============================================
          BEFORE VS AFTER SECTION
          ============================================ */}
      <section className="py-24 bg-gradient-to-b from-void-light/50 to-void">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-display text-3xl sm:text-4xl text-foreground">
              The <span className="text-gradient-teal">Tattva</span> Difference
            </h2>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Before */}
            <ScrollReveal direction="left">
              <div className="relative">
                <div className="glass-card p-8 border-orange/20">
                  <h3 className="text-display text-xl text-orange mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange/20 flex items-center justify-center">
                      <X className="w-5 h-5 text-orange" />
                    </div>
                    Before Tattva
                  </h3>
                  <ul className="space-y-4">
                    {beforeAfterData.before.map((item, i) => (
                      <motion.li 
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                        viewport={{ once: true }}
                        className="flex items-center gap-3 text-muted-foreground"
                      >
                        <div className="w-2 h-2 rounded-full bg-orange/50" />
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            </ScrollReveal>

            {/* After */}
            <ScrollReveal direction="right">
              <div className="relative">
                <div className="glass-card p-8 border-teal/20">
                  <h3 className="text-display text-xl text-teal mb-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal/20 flex items-center justify-center">
                      <Check className="w-5 h-5 text-teal" />
                    </div>
                    With Tattva
                  </h3>
                  <ul className="space-y-4">
                    {beforeAfterData.after.map((item, i) => (
                      <motion.li 
                        key={i}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                        viewport={{ once: true }}
                        className="flex items-center gap-3 text-foreground"
                      >
                        <CheckCircle className="w-5 h-5 text-teal flex-shrink-0" />
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ============================================
          AWARDS SECTION
          ============================================ */}
      <section className="py-16 bg-void border-y border-glass-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-12">
            <h3 className="text-display text-2xl text-foreground">
              Recognized Excellence, <span className="text-gradient-orange">Measurable Impact</span>
            </h3>
          </ScrollReveal>

          <StaggerContainer staggerDelay={0.1} className="flex flex-wrap justify-center gap-6">
            {awards.map((award) => (
              <StaggerItem key={award.name}>
                <motion.div
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="flex items-center gap-3 px-6 py-4 rounded-full border border-glass-border bg-glass-surface"
                >
                  <Award className="w-5 h-5 text-orange" />
                  <div>
                    <span className="font-medium text-foreground">{award.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">{award.year}</span>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ============================================
          DIGITAL TRANSFORMATION SECTION
          ============================================ */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Beyond Software
            </span>
            <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
              Full-Cycle Digital
              <br />
              <span className="text-gradient-teal">Transformation</span>
            </h2>
          </ScrollReveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {digitalTransformationItems.map((item, index) => (
              <ScrollReveal key={item.id} delay={index * 0.1}>
                <InteractiveCard
                  hoverScale={1.03}
                  hoverY={-6}
                  glowColor={item.featured ? 'rgba(0,137,123,0.3)' : 'rgba(255,255,255,0.1)'}
                >
                  <div className={cn(
                    'glass-card p-6 h-48 flex flex-col justify-between relative overflow-hidden',
                    item.featured && 'border-teal/30'
                  )}>
                    {item.featured && (
                      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-teal/20 to-transparent rounded-bl-3xl" />
                    )}
                    <item.icon className={cn(
                      'w-8 h-8',
                      item.featured ? 'text-teal' : 'text-muted-foreground'
                    )} />
                    <div>
                      <h4 className="text-display text-xl text-foreground">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                  </div>
                </InteractiveCard>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Wave Divider */}
      <WaveDivider fillColor="hsl(215, 50%, 6%)" waveColor="hsl(17, 100%, 63%)" flip={true} />

      {/* ============================================
          TESTIMONIALS SECTION
          ============================================ */}
      <section className="py-24 bg-void relative overflow-hidden">
        <LayeredParallax className="absolute inset-0 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <ScrollReveal className="text-center mb-12">
            <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
              Trusted by 500+ Institutions
            </span>
            <h2 className="text-display text-3xl sm:text-4xl lg:text-5xl text-foreground">
              What Our Partners Say
              <br />
              <span className="text-gradient-orange">About Us</span>
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              From convent schools to university networks, hear from the educators
              who have transformed their institutions with Tattva Techworkz.
            </p>
          </ScrollReveal>

          {/* Scrolling Testimonials - Row 1 */}
          <div className="mb-6">
            <InfiniteMovingCards
              items={testimonials.slice(0, 9).map((testimonial, index) => (
                <div key={index} className="glass-card p-6 w-[300px] sm:w-[380px] flex flex-col" style={{ minHeight: '220px' }}>
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-orange text-orange" />
                    ))}
                  </div>
                  <p className="text-foreground mb-4 leading-relaxed flex-1 text-sm line-clamp-4">
                    &quot;{testimonial.quote}&quot;
                  </p>
                  <div className="flex items-center gap-3 mt-auto pt-3 border-t border-glass-border">
                    <div className="w-9 h-9 rounded-full bg-teal/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal font-display font-bold text-xs">
                        {testimonial.author.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-display font-semibold text-foreground truncate">
                        {testimonial.author}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              ))}
              direction="left"
              speed="slow"
            />
          </div>

          {/* Scrolling Testimonials - Row 2 (opposite direction) */}
          <div className="mb-6">
            <InfiniteMovingCards
              items={testimonials.slice(9, 18).map((testimonial, index) => (
                <div key={index} className="glass-card p-6 w-[300px] sm:w-[380px] flex flex-col" style={{ minHeight: '220px' }}>
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-teal text-teal" />
                    ))}
                  </div>
                  <p className="text-foreground mb-4 leading-relaxed flex-1 text-sm line-clamp-4">
                    &quot;{testimonial.quote}&quot;
                  </p>
                  <div className="flex items-center gap-3 mt-auto pt-3 border-t border-glass-border">
                    <div className="w-9 h-9 rounded-full bg-orange/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-orange font-display font-bold text-xs">
                        {testimonial.author.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-display font-semibold text-foreground truncate">
                        {testimonial.author}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              ))}
              direction="right"
              speed="slow"
            />
          </div>

          {/* Scrolling Testimonials - Row 3 */}
          <div className="mb-8">
            <InfiniteMovingCards
              items={testimonials.slice(18).map((testimonial, index) => (
                <div key={index} className="glass-card p-6 w-[300px] sm:w-[380px] flex flex-col" style={{ minHeight: '220px' }}>
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-orange text-orange" />
                    ))}
                  </div>
                  <p className="text-foreground mb-4 leading-relaxed flex-1 text-sm line-clamp-4">
                    &quot;{testimonial.quote}&quot;
                  </p>
                  <div className="flex items-center gap-3 mt-auto pt-3 border-t border-glass-border">
                    <div className="w-9 h-9 rounded-full bg-teal/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal font-display font-bold text-xs">
                        {testimonial.author.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-display font-semibold text-foreground truncate">
                        {testimonial.author}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              ))}
              direction="left"
              speed="normal"
            />
          </div>

          {/* Stats below testimonials */}
          <ScrollReveal className="text-center mt-12">
            <div className="inline-flex flex-wrap justify-center gap-8 px-8 py-6 glass-card">
              <div className="text-center">
                <div className="text-2xl font-display font-bold text-teal">500+</div>
                <div className="text-xs text-muted-foreground">Schools & Colleges</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-display font-bold text-orange">10L+</div>
                <div className="text-xs text-muted-foreground">Students Managed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-display font-bold text-teal">5</div>
                <div className="text-xs text-muted-foreground">Countries</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-display font-bold text-orange">24/7</div>
                <div className="text-xs text-muted-foreground">Support Available</div>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal className="text-center mt-8">
            <Link to="/contact" className="btn-primary inline-flex">
              Schedule a Demo
              <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* ============================================
          GET IN TOUCH SECTION
          ============================================ */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Contact Info */}
            <ScrollReveal direction="left">
              <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-6">
                Get in Touch
              </h2>
              <p className="text-muted-foreground mb-8 max-w-md">
                Ready to transform your institution? Our team of experts is here to help you 
                every step of the way.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal/20 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-teal" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="text-foreground font-medium">+91 89519 64093</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-teal" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="text-foreground font-medium">info@tattvatechworkz.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-teal" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="text-foreground font-medium">Bangalore, India</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            {/* Right - Images */}
            <ScrollReveal direction="right">
              <div className="relative">
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <motion.div
                    className="rounded-2xl overflow-hidden aspect-[3/4]"
                    whileHover={{ scale: 1.05 }}
                  >
                    <img
                      src="/images/team/leo-fernandez.webp"
                      alt="Leo Fernandez - CEO"
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                  <motion.div
                    className="rounded-2xl overflow-hidden aspect-[3/4] mt-8"
                    whileHover={{ scale: 1.05 }}
                  >
                    <img
                      src="/images/team/bambino-clansmann.jpg"
                      alt="Bambino Clansmann - COO"
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                  <motion.div
                    className="rounded-2xl overflow-hidden aspect-[3/4]"
                    whileHover={{ scale: 1.05 }}
                  >
                    <img
                      src="/images/team/shankar-nathan.jpeg"
                      alt="Shankar Nathan - CMO"
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ============================================
          CTA SECTION - Teal to Orange Gradient
          ============================================ */}
      <section className="relative py-24 overflow-hidden bg-void">
        {/* Background */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, hsl(166 100% 37% / 0.15) 0%, hsl(16 100% 60% / 0.15) 100%)' }} />
        <div className="absolute inset-0 bg-void/80" />
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle, rgba(0,191,165,0.15) 1px, transparent 1px)`,
              backgroundSize: '30px 30px',
            }}
          />
        </div>

        {/* Sparkles */}
        <div className="absolute inset-0">
          <SparklesCore
            id="ctaSparkles"
            background="transparent"
            minSize={0.4}
            maxSize={1}
            particleDensity={30}
            particleColor="#00BFA5"
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6">
              Let&apos;s Create Your
              <br />
              <span className="text-gradient-teal">Dream Project</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto">
              Take the first step towards transforming your institution with our cutting-edge
              education technology solutions.
            </p>
            <MagneticButton strength={0.2}>
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-10 py-5 rounded-full font-display font-bold text-lg text-primary-foreground shadow-2xl hover:shadow-teal-glow transition-shadow"
                  style={{ background: 'linear-gradient(135deg, #00BFA5 0%, #FF6B35 100%)' }}
                  data-testid="cta-consultation"
                >
                  Schedule a Free Consultation
                </motion.button>
              </Link>
            </MagneticButton>
          </ScrollReveal>
        </div>
      </section>

      {/* ============================================
          STATS BAR (Final)
          ============================================ */}
      <section className="py-16 bg-void border-t border-glass-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <StaggerContainer staggerDelay={0.1} className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <StaggerItem key={stat.label} className="text-center group">
                <div className="text-3xl sm:text-4xl font-display font-bold text-teal mb-1">
                  <GSAPCounter 
                    value={stat.value}
                    suffix={stat.suffix}
                    duration={2}
                  />
                </div>
                <div className="text-sm text-muted-foreground group-hover:text-teal transition-colors duration-200">
                  {stat.label}
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>
    </div>
  );
};

// X icon for Before section
const X = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default HomePage;
